export interface PlanningWeekDay {
  localDate: string;
  weekday: string;
  shortLabel: string;
}

export interface PlanningMonthDay extends PlanningWeekDay {
  monthColumn: number;
}

export const PLAN_POINTER_DRAG_THRESHOLD = 8;

interface TaskCollectionRecord {
  task: { id: string };
}

export type PlanTaskDropPlacement = "before" | "after";

interface PlanTaskCollectionRecord extends TaskCollectionRecord {
  task: { id: string; planned_local_date: string | null };
  schedule_local_date: string;
}

export function replaceTaskCollectionItem<T extends TaskCollectionRecord>(
  items: T[],
  replacement: TaskCollectionRecord & Partial<T>,
): T[] {
  return items.map((item) =>
    item.task.id === replacement.task.id ? { ...item, ...replacement } : item,
  );
}

export function movePlanTaskToDrop<T extends PlanTaskCollectionRecord>(
  items: T[],
  sourceTaskId: string,
  targetLocalDate: string,
  targetTaskId: string | null,
  placement: PlanTaskDropPlacement | null,
): T[] | null {
  const source = items.find((item) => item.task.id === sourceTaskId);
  if (source === undefined || targetTaskId === sourceTaskId) return null;

  const currentTargetIds = items
    .filter((item) => item.schedule_local_date === targetLocalDate)
    .map((item) => item.task.id);
  const destination = items.filter(
    (item) => item.schedule_local_date === targetLocalDate && item.task.id !== sourceTaskId,
  );
  let insertionIndex = destination.length;
  if (targetTaskId !== null) {
    const targetIndex = destination.findIndex((item) => item.task.id === targetTaskId);
    if (targetIndex >= 0) insertionIndex = targetIndex + (placement === "after" ? 1 : 0);
  }
  const moved = {
    ...source,
    task: { ...source.task, planned_local_date: targetLocalDate },
    schedule_local_date: targetLocalDate,
  };
  const nextTarget = [...destination];
  nextTarget.splice(insertionIndex, 0, moved);
  const nextTargetIds = nextTarget.map((item) => item.task.id);
  if (
    source.schedule_local_date === targetLocalDate &&
    currentTargetIds.length === nextTargetIds.length &&
    currentTargetIds.every((taskId, index) => taskId === nextTargetIds[index])
  ) {
    return null;
  }

  const result: T[] = [];
  let insertedTarget = false;
  for (const item of items) {
    if (item.task.id === sourceTaskId) continue;
    if (item.schedule_local_date === targetLocalDate) {
      if (!insertedTarget) {
        result.push(...nextTarget);
        insertedTarget = true;
      }
      continue;
    }
    result.push(item);
  }
  if (!insertedTarget) result.push(...nextTarget);
  return result;
}

export function planPointerDragActivated(
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
): boolean {
  return Math.hypot(currentX - startX, currentY - startY) >= PLAN_POINTER_DRAG_THRESHOLD;
}

export function planDragAutoScrollDelta(
  pointerX: number,
  containerLeft: number,
  containerRight: number,
  edgeSize = 56,
  maximumDelta = 18,
): number {
  if (containerRight <= containerLeft || edgeSize <= 0 || maximumDelta <= 0) return 0;
  if (pointerX < containerLeft + edgeSize) {
    const intensity = Math.min(1, (containerLeft + edgeSize - pointerX) / edgeSize);
    return -Math.ceil(maximumDelta * intensity);
  }
  if (pointerX > containerRight - edgeSize) {
    const intensity = Math.min(1, (pointerX - (containerRight - edgeSize)) / edgeSize);
    return Math.ceil(maximumDelta * intensity);
  }
  return 0;
}

export function localDateInTimezone(timezone: string, instant: Date | number = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const part = (type: "year" | "month" | "day") =>
    parts.find((candidate) => candidate.type === type)?.value;
  const year = part("year");
  const month = part("month");
  const day = part("day");
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error("The Workspace local date is unavailable.");
  }
  return `${year}-${month}-${day}`;
}

const weekdayFormatter = new Intl.DateTimeFormat("ja-JP", {
  weekday: "short",
  timeZone: "UTC",
});
const dateFormatter = new Intl.DateTimeFormat("ja-JP", {
  month: "numeric",
  day: "numeric",
  timeZone: "UTC",
});

function parseLocalDate(localDate: string): Date {
  const [yearText, monthText, dayText] = localDate.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    !/^\d{4}-\d{2}-\d{2}$/u.test(localDate) ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    throw new Error("A valid local date is required.");
  }
  return date;
}

export function addLocalDays(localDate: string, amount: number): string {
  const date = parseLocalDate(localDate);
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

export function addLocalMonths(localDate: string, amount: number): string {
  const date = parseLocalDate(localDate);
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + amount);
  return date.toISOString().slice(0, 10);
}

export function startOfPlanningWeek(localDate: string): string {
  const date = parseLocalDate(localDate);
  const mondayOffset = (date.getUTCDay() + 6) % 7;
  return addLocalDays(localDate, -mondayOffset);
}

export function planningWeek(localDate: string): PlanningWeekDay[] {
  const start = startOfPlanningWeek(localDate);
  return Array.from({ length: 7 }, (_, index) => {
    const date = addLocalDays(start, index);
    const parsed = parseLocalDate(date);
    return {
      localDate: date,
      weekday: weekdayFormatter.format(parsed),
      shortLabel: dateFormatter.format(parsed),
    };
  });
}

export function planningWeekLabel(localDate: string): string {
  const week = planningWeek(localDate);
  const first = week[0];
  const last = week.at(-1);
  if (first === undefined || last === undefined) throw new Error("Planning week is unavailable.");
  return `${first.shortLabel} 〜 ${last.shortLabel}`;
}

export function planningMonth(localDate: string): PlanningMonthDay[] {
  const anchor = parseLocalDate(localDate);
  const year = anchor.getUTCFullYear();
  const month = anchor.getUTCMonth();
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return Array.from({ length: days }, (_, index) => {
    const date = new Date(Date.UTC(year, month, index + 1));
    const formatted = date.toISOString().slice(0, 10);
    return {
      localDate: formatted,
      weekday: weekdayFormatter.format(date),
      shortLabel: dateFormatter.format(date),
      monthColumn: ((date.getUTCDay() + 6) % 7) + 1,
    };
  });
}

export function planningMonthLabel(localDate: string): string {
  const date = parseLocalDate(localDate);
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(date);
}
